// =============================================================================
// Admin Analytics — data hooks
// =============================================================================
// The Reports tab loads with ONE request: `adminAnalyticsDashboard` fetches
// every cell at the page-level filters, and each cell hook reads its slice of
// it. As soon as a cell has local state of its own (a time override, a category,
// a page past the first), that cell switches to its single-cell operation with
// the cell filters layered on top — so one widget's change re-fetches only that
// widget. The dashboard keeps partial data: a cell the server errored on (or
// returned null for) shows its own error state, and only its "Try again" runs
// that cell's own request — a failing dashboard never fans out on its own.
// Drilldowns fetch a page of rows and, separately, their filter options —
// paging never recomputes the options. The options list the top `FACET_LIMIT`
// values per key, and the dialog's search finds the rest on the server.
//
// No analytics query retries: each one is a scan of the shared database, so a
// retry multiplies exactly the load these queries are capped to avoid. Nothing
// runs until the org details resolve (they decide the scope), and every query
// passes React Query's AbortSignal so a superseded request is cancelled.
//
// Activation still runs through the adapter (`AdminAnalyticsProvider`); there
// is no backend for it yet.
// =============================================================================
import { Query, keepPreviousData, useQuery } from "@tanstack/react-query";
import { Variables } from "graphql-request";
import { useMemo, useState } from "react";

import { CalendarDateRange } from "@fronterahealth/frontera-ui-components";

import {
  AdminAnalyticsDashboardDocument,
  AdminAnalyticsDashboardQuery,
  AdminAnalyticsDashboardQueryVariables,
  AdminAnalyticsOpenQaFlagsDocument,
  AdminAnalyticsOpenQaFlagsQuery,
  AdminAnalyticsOpenQaFlagsQueryVariables,
  AdminAnalyticsOverdueReportFacetsDocument,
  AdminAnalyticsOverdueReportFacetsQuery,
  AdminAnalyticsOverdueReportFacetsQueryVariables,
  AdminAnalyticsOverdueReportRowsDocument,
  AdminAnalyticsOverdueReportRowsQuery,
  AdminAnalyticsOverdueReportRowsQueryVariables,
  AdminAnalyticsOverdueReportsDocument,
  AdminAnalyticsOverdueReportsQuery,
  AdminAnalyticsOverdueReportsQueryVariables,
  AdminAnalyticsQaByUserDocument,
  AdminAnalyticsQaByUserQuery,
  AdminAnalyticsQaByUserQueryVariables,
  AdminAnalyticsQaFlagFacetsDocument,
  AdminAnalyticsQaFlagFacetsQuery,
  AdminAnalyticsQaFlagFacetsQueryVariables,
  AdminAnalyticsQaFlagRowsDocument,
  AdminAnalyticsQaFlagRowsQuery,
  AdminAnalyticsQaFlagRowsQueryVariables,
  AdminAnalyticsReportFacetsDocument,
  AdminAnalyticsReportFacetsQuery,
  AdminAnalyticsReportFacetsQueryVariables,
  AdminAnalyticsReportRowsDocument,
  AdminAnalyticsReportRowsQuery,
  AdminAnalyticsReportRowsQueryVariables,
  AdminAnalyticsReportsByStatusDocument,
  AdminAnalyticsReportsByStatusQuery,
  AdminAnalyticsReportsByStatusQueryVariables,
  AdminAnalyticsReportsByUserDocument,
  AdminAnalyticsReportsByUserQuery,
  AdminAnalyticsReportsByUserQueryVariables,
  AdminAnalyticsResolvedQaFlagsDocument,
  AdminAnalyticsResolvedQaFlagsQuery,
  AdminAnalyticsResolvedQaFlagsQueryVariables,
  AdminAnalyticsSubmissionTrendDocument,
  AdminAnalyticsSubmissionTrendQuery,
  AdminAnalyticsSubmissionTrendQueryVariables,
  AdminAnalyticsTemplateOptionsDocument,
  AdminAnalyticsTemplateOptionsQuery,
  AdminAnalyticsTemplateOptionsQueryVariables,
  AdminAnalyticsTotalReportsDocument,
  AdminAnalyticsTotalReportsQuery,
  AdminAnalyticsTotalReportsQueryVariables,
  AdminAnalyticsWritingTimeDocument,
  AdminAnalyticsWritingTimeQuery,
  AdminAnalyticsWritingTimeQueryVariables,
  AnalyticsCellFiltersInput,
  AnalyticsOverdueReportSortInput,
  AnalyticsOverdueRowFieldsFragment,
  AnalyticsQaFlagRowFieldsFragment,
  AnalyticsQaFlagSortInput,
  AnalyticsReportDateField,
  AnalyticsReportRowFieldsFragment,
  AnalyticsReportSortInput,
} from "@api/graphql/types-and-hooks";
import { useAdminAnalytics } from "@pages/AdminAnalytics/AdminAnalyticsProvider";
import {
  CellFilters,
  FACET_LIMIT,
  FacetSearch,
  PageState,
  buildPageFilters,
  firstPage,
  toCellFiltersInput,
  toFacetSearchInput,
  toPageInput,
} from "@pages/AdminAnalytics/data/analyticsFilters";
import { DashboardFilters, TemplateType } from "@pages/AdminAnalytics/data/types";
import { DrilldownExport, useDrilldownExport } from "@pages/AdminAnalytics/data/useDrilldownExport";
import { PartialResult, usePartialDataFetcher } from "@pages/AdminAnalytics/data/usePartialDataFetcher";
import { rangeKey, toComparisonContract, toRangeContract } from "@pages/AdminAnalytics/helpers";

const ROOT_KEY = "admin-analytics";

/** Rows per page in the in-card tables (Overdue Reports, QA Flags by User). */
export const IN_CELL_PAGE_SIZE = 5;
/** Reports by User is a bar list, not a table — it shows the top N providers and counts the rest. */
export const REPORTS_BY_USER_LIMIT = 8;

/**
 * How long a response stays fresh. The fact tables behind every analytics query
 * only change when the nightly or incremental fact refresh runs, so refetching
 * on a shorter cycle just repeats the same scans on the shared primary.
 */
export const ANALYTICS_STALE_TIME_MS = 5 * 60_000;

const QUERY_OPTS = { refetchOnWindowFocus: false, retry: false, staleTime: ANALYTICS_STALE_TIME_MS } as const;

/**
 * Resolve the effective template-type filter for a query. Single-type orgs
 * (e.g. ABC/DB-only) always scope to their one enabled type even when the user
 * hasn't touched the filter, so their analytics never bleed across type boundaries.
 * Runs synchronously — no useEffect timing gap.
 */
export const resolveTemplateTypes = (
  userSelected: TemplateType[],
  enabledTemplates: TemplateType[],
  ignore: boolean,
): TemplateType[] => {
  if (ignore) return [];
  if (userSelected.length > 0) return userSelected;
  return enabledTemplates.length === 1 ? enabledTemplates : [];
};

// ----- One analytics request --------------------------------------------------

type AnalyticsQueryKey = readonly [typeof ROOT_KEY, string, Variables];

type PlaceholderFn<TData> = (
  previous: PartialResult<TData> | undefined,
  previousQuery: Query<PartialResult<TData>, Error, PartialResult<TData>, AnalyticsQueryKey> | undefined,
) => PartialResult<TData> | undefined;

interface AnalyticsQueryOptions<TData> {
  enabled: boolean;
  placeholderData?: PlaceholderFn<TData>;
  meta?: Record<string, unknown>;
  retryOnMount?: boolean;
  /** See `usePartialDataFetcher`. */
  toastOnFailure?: boolean;
}

/**
 * An analytics operation through the partial-data fetcher. Keyed under
 * `ROOT_KEY`, so it never shares a cache entry with the generated hook of the
 * same operation (whose data has a different shape).
 */
const useAnalyticsQuery = <TData extends { adminAnalytics?: unknown }, TVariables extends Variables>(
  document: string,
  operation: string,
  variables: TVariables,
  { toastOnFailure, ...options }: AnalyticsQueryOptions<TData>,
) => {
  const fetchPartial = usePartialDataFetcher<TData, TVariables>(document, { toastOnFailure });
  return useQuery<PartialResult<TData>, Error, PartialResult<TData>, AnalyticsQueryKey>({
    queryKey: [ROOT_KEY, operation, variables],
    queryFn: ({ signal }) => fetchPartial(variables, signal),
    ...QUERY_OPTS,
    ...options,
  });
};

// ----- Page-level filters + the dashboard request ----------------------------

/** The sticky filter bar as `AnalyticsFiltersInput`. */
export const usePageFilters = () => {
  const { globalRange, templateType, templateName, orgConfig } = useAdminAnalytics();
  return useMemo(
    () =>
      buildPageFilters(
        globalRange,
        resolveTemplateTypes(templateType, orgConfig.enabledTemplates, false),
        templateName,
      ),
    [globalRange, templateType, templateName, orgConfig.enabledTemplates],
  );
};

/**
 * The single dashboard request. Every cell observes it with the same constant
 * `enabled` (and no retry-on-mount), so a cell switching between the dashboard
 * and its own request never makes the dashboard refetch. A whole-request
 * failure toasts once; the cells show their own error states.
 */
const useAnalyticsDashboard = () => {
  const { selectedTab, orgConfig, orgReady } = useAdminAnalytics();
  const filters = usePageFilters();
  const variables: AdminAnalyticsDashboardQueryVariables = {
    filters,
    reportsByUserPage: firstPage(REPORTS_BY_USER_LIMIT),
    overduePage: firstPage(IN_CELL_PAGE_SIZE),
    qaByUserPage: firstPage(IN_CELL_PAGE_SIZE),
    includeSubmissionTrend: orgConfig.isCatalightOrg,
  };
  return useAnalyticsQuery<AdminAnalyticsDashboardQuery, AdminAnalyticsDashboardQueryVariables>(
    AdminAnalyticsDashboardDocument,
    "dashboard",
    variables,
    {
      enabled: orgReady && selectedTab === "Reports",
      retryOnMount: false,
      placeholderData: keepPreviousData,
      toastOnFailure: true,
    },
  );
};

/**
 * Narrow facets whose own filter would otherwise escape the page-level scope:
 * picking a report type or template in a drilldown replaces the page-level
 * value for that dialog, so only offer (and send) values the page allows. A
 * cell's own report-type override (the writing-time card) replaces the page
 * value on purpose, so when it's set it becomes the scope instead.
 */
export const useFacetScope = (cell: CellFilters = {}) => {
  const page = usePageFilters();
  const cellTypes = toCellFiltersInput({ reportTypes: cell.reportTypes })?.reportTypes;
  return {
    reportType: cellTypes ?? page.reportTypes ?? undefined,
    templateGroup: page.templateGroups ?? undefined,
  };
};

// ----- Cells -----------------------------------------------------------------

type AnalyticsRoot = NonNullable<AdminAnalyticsDashboardQuery["adminAnalytics"]>;
type CellKey = keyof Omit<AnalyticsRoot, "__typename">;
type CellData<K extends CellKey> = { adminAnalytics?: Partial<Pick<AnalyticsRoot, K>> | null };

interface QueryState<TData> {
  data?: PartialResult<TData>;
  isFetching: boolean;
  isPlaceholderData: boolean;
  refetch: () => unknown;
}

export interface CellResult<T> {
  data: T | undefined;
  isFetching: boolean;
  /** The cell couldn't load — its request failed, or the server returned no value for it. */
  isError: boolean;
  /** "Try again": re-runs the cell's own request (or starts it, if the cell was reading the dashboard). */
  refetch: () => void;
}

/** A settled response for the current variables has no value for `key`. */
const missedCell = <K extends CellKey>(
  key: K,
  result: PartialResult<CellData<K>> | undefined,
  isPlaceholderData: boolean,
): boolean =>
  !!result &&
  !isPlaceholderData &&
  (result.requestFailed || result.failedFields.includes(key) || result.data?.adminAnalytics?.[key] == null);

const isDashboardPage = ({ page, pageSize }: PageState) => page === 1 && pageSize === IN_CELL_PAGE_SIZE;

/**
 * Where a cell gets its data: its own operation when it has local state, or
 * once its "Try again" was clicked after the dashboard couldn't load it (for
 * the current page filters only — a filter change hands it back to the
 * dashboard). Otherwise the dashboard, even when that failed.
 */
const useCellSource = (key: CellKey, cell: CellFilters, page?: PageState) => {
  const { orgReady } = useAdminAnalytics();
  const filters = usePageFilters();
  const cellInput = toCellFiltersInput(cell);
  const hasLocalState = cellInput !== undefined || (page !== undefined && !isDashboardPage(page));
  const dashboard = useAnalyticsDashboard();
  const filtersKey = JSON.stringify(filters);
  const [retriedFor, setRetriedFor] = useState<string | undefined>(undefined);
  return {
    key,
    filters,
    cell: cellInput,
    dashboard,
    orgReady,
    own: hasLocalState || retriedFor === filtersKey,
    retryOnOwn: () => setRetriedFor(filtersKey),
  };
};

type CellSource = ReturnType<typeof useCellSource>;

const pickCell = <K extends CellKey>(
  key: K,
  source: CellSource,
  cellQuery: QueryState<CellData<K>>,
): CellResult<NonNullable<AnalyticsRoot[K]>> => {
  const query = source.own ? cellQuery : source.dashboard;
  const result = query.data as PartialResult<CellData<K>> | undefined;
  const failed = missedCell(key, result, query.isPlaceholderData);
  return {
    data: failed ? undefined : ((result?.data?.adminAnalytics?.[key] ?? undefined) as NonNullable<AnalyticsRoot[K]>),
    isFetching: query.isFetching,
    isError: failed && !query.isFetching,
    refetch: source.own ? () => void cellQuery.refetch() : source.retryOnOwn,
  };
};

const cellQueryOptions = <TData>(
  source: CellSource,
  placeholderData: AnalyticsQueryOptions<TData>["placeholderData"] = keepPreviousData,
): AnalyticsQueryOptions<TData> => ({ enabled: source.orgReady && source.own, placeholderData });

/**
 * While a paged cell's own request loads, show the dashboard's first page if
 * that's what the cell was just displaying (no cell filters), so the table
 * keeps its rows instead of dropping to a skeleton.
 */
const dashboardPlaceholder =
  <K extends "overdueReports" | "qaByUser", TData extends CellData<K>>(key: K, source: CellSource) =>
  (previous: PartialResult<TData> | undefined): PartialResult<TData> | undefined => {
    if (previous) return previous;
    const slice = source.cell === undefined ? source.dashboard.data?.data?.adminAnalytics?.[key] : undefined;
    return slice
      ? { data: { adminAnalytics: { [key]: slice } } as TData, failedFields: [], requestFailed: false }
      : undefined;
  };

export const useTotalReportsCell = (cell: CellFilters) => {
  const source = useCellSource("totalReports", cell);
  const query = useAnalyticsQuery<AdminAnalyticsTotalReportsQuery, AdminAnalyticsTotalReportsQueryVariables>(
    AdminAnalyticsTotalReportsDocument,
    "totalReports",
    { filters: source.filters, cell: source.cell },
    cellQueryOptions(source),
  );
  return pickCell("totalReports", source, query);
};

export const useOpenQaFlagsCell = (cell: CellFilters) => {
  const source = useCellSource("openQaFlags", cell);
  const query = useAnalyticsQuery<AdminAnalyticsOpenQaFlagsQuery, AdminAnalyticsOpenQaFlagsQueryVariables>(
    AdminAnalyticsOpenQaFlagsDocument,
    "openQaFlags",
    { filters: source.filters, cell: source.cell },
    cellQueryOptions(source),
  );
  return pickCell("openQaFlags", source, query);
};

export const useResolvedQaFlagsCell = (cell: CellFilters) => {
  const source = useCellSource("resolvedQaFlags", cell);
  const query = useAnalyticsQuery<AdminAnalyticsResolvedQaFlagsQuery, AdminAnalyticsResolvedQaFlagsQueryVariables>(
    AdminAnalyticsResolvedQaFlagsDocument,
    "resolvedQaFlags",
    { filters: source.filters, cell: source.cell },
    cellQueryOptions(source),
  );
  return pickCell("resolvedQaFlags", source, query);
};

export const useReportsByStatusCell = (cell: CellFilters) => {
  const source = useCellSource("reportsByStatus", cell);
  const query = useAnalyticsQuery<AdminAnalyticsReportsByStatusQuery, AdminAnalyticsReportsByStatusQueryVariables>(
    AdminAnalyticsReportsByStatusDocument,
    "reportsByStatus",
    { filters: source.filters, cell: source.cell },
    cellQueryOptions(source),
  );
  return pickCell("reportsByStatus", source, query);
};

export const useWritingTimeCell = (cell: CellFilters) => {
  const source = useCellSource("writingTimeByReportType", cell);
  const query = useAnalyticsQuery<AdminAnalyticsWritingTimeQuery, AdminAnalyticsWritingTimeQueryVariables>(
    AdminAnalyticsWritingTimeDocument,
    "writingTime",
    { filters: source.filters, cell: source.cell },
    cellQueryOptions(source),
  );
  return pickCell("writingTimeByReportType", source, query);
};

export const useSubmissionTrendCell = (cell: CellFilters) => {
  const source = useCellSource("submissionTrend", cell);
  const query = useAnalyticsQuery<AdminAnalyticsSubmissionTrendQuery, AdminAnalyticsSubmissionTrendQueryVariables>(
    AdminAnalyticsSubmissionTrendDocument,
    "submissionTrend",
    { filters: source.filters, cell: source.cell },
    cellQueryOptions(source),
  );
  return pickCell("submissionTrend", source, query);
};

export const useReportsByUserCell = (cell: CellFilters) => {
  const source = useCellSource("reportsByUser", cell);
  const query = useAnalyticsQuery<AdminAnalyticsReportsByUserQuery, AdminAnalyticsReportsByUserQueryVariables>(
    AdminAnalyticsReportsByUserDocument,
    "reportsByUser",
    { filters: source.filters, cell: source.cell, page: firstPage(REPORTS_BY_USER_LIMIT) },
    cellQueryOptions(source),
  );
  return pickCell("reportsByUser", source, query);
};

export const useOverdueReportsCell = (cell: CellFilters, page: PageState) => {
  const source = useCellSource("overdueReports", cell, page);
  const query = useAnalyticsQuery<AdminAnalyticsOverdueReportsQuery, AdminAnalyticsOverdueReportsQueryVariables>(
    AdminAnalyticsOverdueReportsDocument,
    "overdueReports",
    { filters: source.filters, cell: source.cell, page: toPageInput(page) },
    cellQueryOptions(source, dashboardPlaceholder("overdueReports", source)),
  );
  return pickCell("overdueReports", source, query);
};

export const useQaByUserCell = (cell: CellFilters, page: PageState) => {
  const source = useCellSource("qaByUser", cell, page);
  const query = useAnalyticsQuery<AdminAnalyticsQaByUserQuery, AdminAnalyticsQaByUserQueryVariables>(
    AdminAnalyticsQaByUserDocument,
    "qaByUser",
    { filters: source.filters, cell: source.cell, page: toPageInput(page) },
    cellQueryOptions(source, dashboardPlaceholder("qaByUser", source)),
  );
  return pickCell("qaByUser", source, query);
};

/**
 * Template names in scope, for the page-level Template filter. Only fetched on
 * the Reports tab. The dropdown has no error state of its own, so when the
 * dashboard answered but couldn't return this one cell, a single bounded
 * request fetches it. After a whole-dashboard failure (when FDM is most likely
 * overloaded) the dropdown stays empty until the dashboard is requested again.
 */
export const useTemplateOptions = () => {
  const { selectedTab } = useAdminAnalytics();
  const source = useCellSource("templateOptions", {});
  const dashboard = source.dashboard.data;
  const missed =
    !!dashboard &&
    !dashboard.requestFailed &&
    missedCell("templateOptions", dashboard, source.dashboard.isPlaceholderData);
  const fallback = { ...source, own: source.own || missed };
  const query = useAnalyticsQuery<AdminAnalyticsTemplateOptionsQuery, AdminAnalyticsTemplateOptionsQueryVariables>(
    AdminAnalyticsTemplateOptionsDocument,
    "templateOptions",
    { filters: source.filters },
    { ...cellQueryOptions(fallback), enabled: fallback.orgReady && fallback.own && selectedTab === "Reports" },
  );
  return pickCell("templateOptions", fallback, query).data;
};

// ----- Drilldowns: a page of rows + (separately) the filter options ----------

interface DrilldownArgs<TSort> {
  open: boolean;
  /** The dialog's `ServerDrilldown.session`; scopes the facets placeholder to one open. */
  session: number;
  cell: AnalyticsCellFiltersInput;
  page: PageState;
  sort: TSort;
  /** The dialog's settled facet search (`ServerDrilldown.query.facetSearch`). */
  facetSearch?: FacetSearch;
}

type RowOf<TPage> = TPage extends { rows: readonly (infer TRow)[] } ? TRow : never;

export interface DrilldownResult<TPage, TFacets> {
  page: TPage | undefined;
  facets: TFacets | undefined;
  isFetching: boolean;
  /** Rows are loading and there's no page for these filters to keep on screen. */
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  facetsError: boolean;
  refetchFacets: () => void;
  /** Every row of the current result, fetched page by page for the CSV. */
  export: DrilldownExport<RowOf<TPage>>;
}

const withoutPage = (variables: unknown): string => {
  const { page: _page, ...rest } = (variables ?? {}) as Record<string, unknown>;
  return JSON.stringify(rest);
};

/**
 * Keep the previous rows on screen only while paging within the same filters.
 * Any other change — a new seed, new selections, a new dialog session — has no
 * placeholder, so the count and export can never describe a different query.
 */
const keepPreviousPage =
  <TData>(variables: Variables): PlaceholderFn<TData> =>
  (previous, previousQuery) =>
    previousQuery && withoutPage(previousQuery.queryKey[2]) === withoutPage(variables) ? previous : undefined;

/**
 * Keep the previous filter options while the same dialog session changes its
 * filters, so the open value list doesn't blank out on every toggle. A new
 * session (the dialog reopened) never shows the previous session's options.
 */
const keepWithinSession =
  <TData>(session: number): PlaceholderFn<TData> =>
  (previous, previousQuery) =>
    previousQuery?.meta?.drilldownSession === session ? previous : undefined;

/** Top `FACET_LIMIT` values per facet key, narrowed by the dialog's search. */
const facetArgs = (search: FacetSearch = {}) => {
  const facetSearch = toFacetSearchInput(search);
  return { facetLimit: FACET_LIMIT, ...(facetSearch ? { facetSearch } : {}) };
};

const facetsOptions = <TData>(open: boolean, orgReady: boolean, session: number): AnalyticsQueryOptions<TData> => ({
  enabled: open && orgReady,
  placeholderData: keepWithinSession<TData>(session),
  meta: { drilldownSession: session },
});

const drilldownResult = <TPage, TFacets>(
  field: string,
  rows: QueryState<{ adminAnalytics?: unknown }> & { isPending: boolean },
  page: TPage | null | undefined,
  facetsQuery: QueryState<{ adminAnalytics?: unknown }>,
  facets: TFacets | null | undefined,
  exportAll: DrilldownExport<RowOf<TPage>>,
): DrilldownResult<TPage, TFacets> => {
  const settled = !!rows.data && !rows.isPlaceholderData && !rows.isFetching;
  const facetsSettled = !!facetsQuery.data && !facetsQuery.isFetching;
  return {
    page: page ?? undefined,
    facets: facets ?? undefined,
    isFetching: rows.isFetching,
    isLoading: !page && (rows.isFetching || rows.isPending),
    isError: settled && (!!rows.data?.requestFailed || !!rows.data?.failedFields.includes(field) || !page),
    refetch: () => void rows.refetch(),
    facetsError:
      facetsSettled &&
      (!!facetsQuery.data?.requestFailed || !!facetsQuery.data?.failedFields.includes(field) || !facets),
    refetchFacets: () => void facetsQuery.refetch(),
    export: exportAll,
  };
};

export const useReportDrilldown = ({
  open,
  session,
  cell,
  page,
  sort,
  facetSearch,
  dateField,
}: DrilldownArgs<AnalyticsReportSortInput> & { dateField: AnalyticsReportDateField }) => {
  const { orgReady } = useAdminAnalytics();
  const filters = usePageFilters();
  const rowVariables = { filters, cell, dateField, page: toPageInput(page), sort };
  const rows = useAnalyticsQuery<AdminAnalyticsReportRowsQuery, AdminAnalyticsReportRowsQueryVariables>(
    AdminAnalyticsReportRowsDocument,
    "reportRows",
    rowVariables,
    { enabled: open && orgReady, placeholderData: keepPreviousPage(rowVariables) },
  );
  const facets = useAnalyticsQuery<AdminAnalyticsReportFacetsQuery, AdminAnalyticsReportFacetsQueryVariables>(
    AdminAnalyticsReportFacetsDocument,
    "reportFacets",
    { filters, cell, dateField, ...facetArgs(facetSearch) },
    facetsOptions(open, orgReady, session),
  );
  const exportAll = useDrilldownExport<
    AdminAnalyticsReportRowsQuery,
    AdminAnalyticsReportRowsQueryVariables,
    AnalyticsReportRowFieldsFragment
  >({
    document: AdminAnalyticsReportRowsDocument,
    variables: { filters, cell, dateField, sort },
    open,
    pageOf: (data) => data?.adminAnalytics?.reports,
  });
  return drilldownResult(
    "reports",
    rows,
    rows.data?.data?.adminAnalytics?.reports,
    facets,
    facets.data?.data?.adminAnalytics?.reports?.facets,
    exportAll,
  );
};

export const useQaFlagDrilldown = ({
  open,
  session,
  cell,
  page,
  sort,
  facetSearch,
}: DrilldownArgs<AnalyticsQaFlagSortInput>) => {
  const { orgReady } = useAdminAnalytics();
  const filters = usePageFilters();
  const rowVariables = { filters, cell, page: toPageInput(page), sort };
  const rows = useAnalyticsQuery<AdminAnalyticsQaFlagRowsQuery, AdminAnalyticsQaFlagRowsQueryVariables>(
    AdminAnalyticsQaFlagRowsDocument,
    "qaFlagRows",
    rowVariables,
    { enabled: open && orgReady, placeholderData: keepPreviousPage(rowVariables) },
  );
  const facets = useAnalyticsQuery<AdminAnalyticsQaFlagFacetsQuery, AdminAnalyticsQaFlagFacetsQueryVariables>(
    AdminAnalyticsQaFlagFacetsDocument,
    "qaFlagFacets",
    { filters, cell, ...facetArgs(facetSearch) },
    facetsOptions(open, orgReady, session),
  );
  const exportAll = useDrilldownExport<
    AdminAnalyticsQaFlagRowsQuery,
    AdminAnalyticsQaFlagRowsQueryVariables,
    AnalyticsQaFlagRowFieldsFragment
  >({
    document: AdminAnalyticsQaFlagRowsDocument,
    variables: { filters, cell, sort },
    open,
    pageOf: (data) => data?.adminAnalytics?.qaFlags,
  });
  return drilldownResult(
    "qaFlags",
    rows,
    rows.data?.data?.adminAnalytics?.qaFlags,
    facets,
    facets.data?.data?.adminAnalytics?.qaFlags?.facets,
    exportAll,
  );
};

export const useOverdueReportDrilldown = ({
  open,
  session,
  cell,
  page,
  sort,
  facetSearch,
}: DrilldownArgs<AnalyticsOverdueReportSortInput>) => {
  const { orgReady } = useAdminAnalytics();
  const filters = usePageFilters();
  const rowVariables = { filters, cell, page: toPageInput(page), sort };
  const rows = useAnalyticsQuery<AdminAnalyticsOverdueReportRowsQuery, AdminAnalyticsOverdueReportRowsQueryVariables>(
    AdminAnalyticsOverdueReportRowsDocument,
    "overdueReportRows",
    rowVariables,
    { enabled: open && orgReady, placeholderData: keepPreviousPage(rowVariables) },
  );
  const facets = useAnalyticsQuery<
    AdminAnalyticsOverdueReportFacetsQuery,
    AdminAnalyticsOverdueReportFacetsQueryVariables
  >(
    AdminAnalyticsOverdueReportFacetsDocument,
    "overdueReportFacets",
    { filters, cell, ...facetArgs(facetSearch) },
    facetsOptions(open, orgReady, session),
  );
  const exportAll = useDrilldownExport<
    AdminAnalyticsOverdueReportRowsQuery,
    AdminAnalyticsOverdueReportRowsQueryVariables,
    AnalyticsOverdueRowFieldsFragment
  >({
    document: AdminAnalyticsOverdueReportRowsDocument,
    variables: { filters, cell, sort },
    open,
    pageOf: (data) => data?.adminAnalytics?.overdueReports,
  });
  return drilldownResult(
    "overdueReports",
    rows,
    rows.data?.data?.adminAnalytics?.overdueReports,
    facets,
    facets.data?.data?.adminAnalytics?.overdueReports?.facets,
    exportAll,
  );
};

// ----- Activation (adapter-backed; template-agnostic) -------------------------

const useActivationFilters = (override: CalendarDateRange | undefined) => {
  const { globalRange } = useAdminAnalytics();
  const range = override ?? globalRange;
  const filters: DashboardFilters = {
    range: toRangeContract(range),
    comparisonRange: toComparisonContract(range),
    templateType: [],
    templateName: [],
  };
  return { filters, key: rangeKey(range) };
};

export const useInviteFunnel = (override?: CalendarDateRange) => {
  const { adapter } = useAdminAnalytics();
  const { filters, key } = useActivationFilters(override);
  return useQuery({
    queryKey: [ROOT_KEY, "invite-funnel", key],
    queryFn: () => adapter.getInviteFunnel(filters),
    ...QUERY_OPTS,
  });
};

export const useActivationProviders = (override?: CalendarDateRange) => {
  const { adapter } = useAdminAnalytics();
  const { filters, key } = useActivationFilters(override);
  return useQuery({
    queryKey: [ROOT_KEY, "activation-providers", key],
    queryFn: () => adapter.getActivationProviders(filters),
    ...QUERY_OPTS,
  });
};
